import React from "react";
import { useLocation } from "react-router-dom";
import { useT } from "../context/LanguageContext";
import {
  CalendarDays,
  Music2,
  Trophy,
  Receipt,
  Award,
  Gift,
  Piano as PianoIcon,
  ChevronDown,
} from "lucide-react";

/**
 * Auth-page demo strip.
 *
 * Sits below the sign-in / register-choice card on the auth pages so brand-new
 * visitors get a fast, scrollable overview of what Octave actually does before
 * they decide to sign up. Each preview is a hand-built mini-mock (not a real
 * screenshot) so it always matches the active theme and never goes stale.
 */
export default function AuthDemoStrip() {
  const { pathname } = useLocation();
  const t = useT();

  return (
    <div className="mt-12 lg:mt-16 mx-auto max-w-3xl" data-testid="auth-demo-strip">
      <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mt-2">
        {t("See what Octave does")}
      </h2>
      <p
        className="text-center mt-2 text-sm sm:text-base"
        style={{ color: "var(--app-text-muted)" }}
      >
        {t("A quick tour — each tile is a glimpse of a real screen inside the app.")}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8">
        <FeatureCard
          tint="#0077B6"
          icon={<CalendarDays className="w-5 h-5" />}
          title={t("Smart calendar & lesson punch-card")}
          blurb={t("Auto-populated month + day view. Students stamp every practice session right from their dashboard.")}
          preview={<CalendarPreview />}
        />
        <FeatureCard
          tint="#FFB703"
          icon={<Trophy className="w-5 h-5" />}
          title={t("Trophies, streaks & shareable milestones")}
          blurb={t("Mastered scales and songs unlock zooming-trophy celebrations + a one-tap shareable image.")}
          preview={<TrophyPreview />}
        />
        <FeatureCard
          tint="#06D6A0"
          icon={<Music2 className="w-5 h-5" />}
          title={t("Songs & scales, on every instrument")}
          blurb={t("Track progress across every instrument in the world — one library, per-instrument tabs.")}
          preview={<SongsPreview />}
        />
        <FeatureCard
          tint="#9333EA"
          icon={<Receipt className="w-5 h-5" />}
          title={t("Recurring invoicing — set it, forget it")}
          blurb={t("Weekly, biweekly, or monthly. We backfill late invoices automatically.")}
          preview={<InvoicePreview />}
        />
        <FeatureCard
          tint="#EE9B00"
          icon={<Award className="w-5 h-5" />}
          title={t("Plan recitals, broadcast updates")}
          blurb={t("Multi-venue photos, sponsors, and one-tap messaging to every attendee.")}
          preview={<RecitalPreview />}
        />
        <FeatureCard
          tint="#EF476F"
          icon={<Gift className="w-5 h-5" />}
          title={t("Earn a free month per friend")}
          blurb={t("Every friend who subscribes adds 30 days to your plan. No cap.")}
          preview={<ReferralPreview />}
        />
      </div>

      <div className="mt-10 rounded-3xl p-6 sm:p-8 text-center border-2"
           style={{ borderColor: "var(--app-border)", background: "var(--app-surface-2)" }}>
        <img src="/octave-icon.png" alt="" aria-hidden className="w-20 h-20 mx-auto object-contain" />
        <div className="mt-4 flex gap-2 justify-center flex-wrap">
          <a
            href={pathname.startsWith("/register") ? "#top" : "/register"}
            onClick={(e) => {
              if (pathname.startsWith("/register")) {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="btn-pill btn-primary h-11 px-6 inline-flex items-center"
            data-testid="demo-cta-back-to-top"
          >
            {t("Start free trial →")}
          </a>
          <a
            href={pathname === "/login" ? "#top" : "/login"}
            onClick={(e) => {
              if (pathname === "/login") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="btn-pill btn-outline h-11 px-6 inline-flex items-center"
            data-testid="demo-cta-login"
          >
            {t("I already have an account")}
          </a>
        </div>
      </div>
    </div>
  );
}

/* ─── building blocks ─────────────────────────────────────────────────────── */

function ScrollHint() {
  const t = useT();
  return (
    <a
      href="#auth-demo-strip"
      onClick={(e) => {
        e.preventDefault();
        const el = document.querySelector('[data-testid="auth-demo-strip"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
      className="flex flex-col items-center mb-2"
      data-testid="see-demo-below"
    >
      <span
        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-bold animate-bounce-slow"
        style={{
          background: "linear-gradient(135deg, var(--app-accent), #FFD166)",
          color: "#3d2a00",
          boxShadow: "0 4px 12px rgba(255,183,3,0.35)",
        }}
      >
        <ChevronDown className="w-4 h-4" /> {t("See demo below")}
      </span>
    </a>
  );
}
export { ScrollHint };

function FeatureCard({ tint, icon, title, blurb, preview }) {
  return (
    <div
      className="rounded-3xl border-2 p-4"
      style={{ borderColor: "var(--app-border)", background: "var(--app-surface)" }}
    >
      <div className="aspect-[16/10] rounded-2xl overflow-hidden mb-4 relative"
           style={{ background: "var(--app-surface-2)", border: "1px solid var(--app-border)" }}>
        {preview}
      </div>
      <div className="flex items-start gap-3">
        <span
          className="flex items-center justify-center rounded-xl w-10 h-10 shrink-0"
          style={{ background: tint, color: "#fff" }}
        >
          {icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base sm:text-lg leading-tight">{title}</div>
          <div className="text-xs sm:text-sm mt-1" style={{ color: "var(--app-text-muted)" }}>{blurb}</div>
        </div>
      </div>
    </div>
  );
}

/* ─── mini "screenshot" mocks (inline SVG-ish JSX) ────────────────────────── */

function CalendarPreview() {
  const t = useT();
  const days = Array.from({ length: 35 }, (_, i) => i - 2); // -2..32
  const today = 14;
  const hits = new Set([3, 5, 8, 10, 13, 14, 17, 19, 21]);
  return (
    <div className="absolute inset-0 p-3 grid grid-rows-[auto_1fr] gap-2">
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-xs">{t("Sept")}</div>
        <div className="flex gap-1">
          <span className="w-2 h-2 rounded-full" style={{ background: "var(--app-primary)" }} />
          <span className="w-2 h-2 rounded-full" style={{ background: "var(--app-accent)" }} />
        </div>
      </div>
      <div className="grid grid-cols-7 gap-[3px]">
        {days.map((d, i) => {
          const inMonth = d > 0 && d <= 30;
          const isHit = inMonth && hits.has(d);
          const isToday = inMonth && d === today;
          return (
            <div
              key={i}
              className="rounded-[4px] flex items-center justify-center text-[8px] font-bold"
              style={{
                background: isToday
                  ? "var(--app-primary)"
                  : isHit ? "rgba(6,214,160,0.30)"
                  : inMonth ? "var(--app-surface)" : "transparent",
                color: isToday ? "#fff" : "var(--app-text)",
                border: inMonth ? "1px solid var(--app-border)" : "none",
                aspectRatio: "1",
              }}
            >
              {inMonth ? d : ""}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrophyPreview() {
  const t = useT();
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
      <div
        className="text-4xl sm:text-5xl"
        style={{ filter: "drop-shadow(0 4px 12px rgba(255,183,3,0.6))" }}
      >🏆</div>
      <div className="text-[10px] sm:text-xs font-display font-bold tracking-widest uppercase">{t("Mastered!")}</div>
      <div className="flex items-baseline gap-3 text-[11px]">
        <span><b>9</b><span style={{ color: "var(--app-text-muted)" }}>/12 {t("scales")}</span></span>
        <span><b>23</b><span style={{ color: "var(--app-text-muted)" }}> {t("songs")}</span></span>
      </div>
      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-bold"
           style={{ background: "var(--app-accent)", color: "#3d2a00" }}>
        +1 {t("streak")}
      </div>
    </div>
  );
}

function SongsPreview() {
  const t = useT();
  const songs = [
    { title: "Clair de Lune", inst: "🎹", pct: 80 },
    { title: "Wonderwall", inst: "🎸", pct: 95 },
    { title: "Pachelbel Canon", inst: "🎻", pct: 40 },
  ];
  return (
    <div className="absolute inset-0 p-3 flex flex-col gap-2">
      <div className="flex gap-1">
        {["🎹", "🎸", "🎻", "🥁"].map((e, i) => (
          <span key={i}
                className="px-2 py-0.5 rounded-full text-[10px]"
                style={{
                  background: i === 0 ? "var(--app-primary)" : "var(--app-surface)",
                  color: i === 0 ? "#fff" : "var(--app-text)",
                  border: i === 0 ? "none" : "1px solid var(--app-border)",
                }}>{e}</span>
        ))}
      </div>
      {songs.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-sm">{s.inst}</span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] truncate font-bold">{s.title}</div>
            <div className="h-1 rounded-full mt-0.5" style={{ background: "var(--app-border)" }}>
              <div className="h-full rounded-full" style={{ width: `${s.pct}%`, background: "var(--app-primary)" }} />
            </div>
          </div>
          <div className="text-[10px] font-bold" style={{ color: "var(--app-primary)" }}>{s.pct}%</div>
        </div>
      ))}
    </div>
  );
}

function InvoicePreview() {
  const t = useT();
  return (
    <div className="absolute inset-0 p-3 flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-xs">{t("Invoice")} #421</div>
        <span className="px-2 py-0.5 rounded-full text-[9px] font-bold"
              style={{ background: "rgba(6,214,160,0.18)", color: "#0a6f4d" }}>{t("PAID")}</span>
      </div>
      <div className="text-[10px]" style={{ color: "var(--app-text-muted)" }}>Avery K. · {t("Sept")} 14</div>
      <div className="border-t my-1" style={{ borderColor: "var(--app-border)" }} />
      <Row label={`${t("Lesson")} · 30 min`} amt="$35.00" />
      <Row label={`${t("Lesson")} · 30 min`} amt="$35.00" />
      <Row label={t("Recital fee")} amt="$10.00" muted />
      <div className="border-t my-1" style={{ borderColor: "var(--app-border)" }} />
      <div className="flex items-center justify-between font-bold text-[11px]">
        <span>{t("Total")}</span><span>$80.00</span>
      </div>
      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-bold"
           style={{ background: "var(--app-primary)", color: "#fff" }}>
        {t("Auto-bills weekly")}
      </div>
    </div>
  );
}
function Row({ label, amt, muted }) {
  return (
    <div className="flex items-center justify-between text-[10px]">
      <span style={{ color: muted ? "var(--app-text-muted)" : "var(--app-text)" }}>{label}</span>
      <span className="font-mono">{amt}</span>
    </div>
  );
}

function RecitalPreview() {
  const t = useT();
  return (
    <div className="absolute inset-0 p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <PianoIcon className="w-4 h-4" style={{ color: "var(--app-primary)" }} />
        <div className="font-display font-bold text-xs">{t("Spring Recital")}</div>
      </div>
      <div className="text-[10px]" style={{ color: "var(--app-text-muted)" }}>{t("May")} 18 · {t("Riverside Hall")} · 23 {t("attendees")}</div>
      <div className="grid grid-cols-3 gap-1">
        {["🎹","🎻","🎸"].map((e,i) => (
          <div key={i} className="rounded-md aspect-[4/3] flex items-center justify-center text-2xl"
               style={{ background: "var(--app-surface)", border: "1px solid var(--app-border)" }}>{e}</div>
        ))}
      </div>
      <div className="rounded-lg p-1.5 text-[10px] font-semibold flex items-center gap-1"
           style={{ background: "rgba(255,183,3,0.18)", color: "#7a5a00" }}>
        📣 {t("Broadcast: Doors open 6:30pm.")}
      </div>
    </div>
  );
}

function ReferralPreview() {
  const t = useT();
  return (
    <div className="absolute inset-0 p-3 flex flex-col items-center justify-center gap-2">
      <div className="text-2xl">🎁</div>
      <div className="font-display font-black text-xl leading-none">+30 {t("days")}</div>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{t("free month earned")}</div>
      <div className="rounded-md px-2 py-1 mt-1 text-[10px] font-mono"
           style={{ background: "var(--app-surface)", border: "1px solid var(--app-border)" }}>
        octavestretch.com/?ref=ME8X3K
      </div>
    </div>
  );
}
